import { useCallback, useEffect, useState } from "react";

import { Modal } from "@/components/ui/overlay/Modal";
import { Button } from "@/components/ui/button/Button";
import { UnavailablePanel } from "@/components/ui/feedback/Unavailable";
import {
  administrationApi,
  type InviteWorkspaceRole,
} from "@/features/administration/api/administration.api";
import { listWorkspaces, type Workspace } from "@/features/workspaces/api";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";

type InviteOrgRole = "admin" | "member" | "viewer";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

/**
 * Single-invite flow: email, display name (stored client-side until Stream A accepts name),
 * and org platform role.
 */
export function InviteOrgMemberDialog({
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<InviteOrgRole>("member");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteDone, setInviteDone] = useState<{ email: string; expiresAt: string | null } | null>(null);
  // J1-INVITE-WS-1: the workspaces to add the person to, each with its own role.
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [wsLoad, setWsLoad] = useState<"loading" | "ready" | "failed">("loading");
  const [picked, setPicked] = useState<Record<string, InviteWorkspaceRole>>({});

  const loadWorkspaces = useCallback(async (): Promise<void> => {
    setWsLoad("loading");
    try {
      const all = await listWorkspaces();
      setWorkspaces((Array.isArray(all) ? all : []).filter((w) => !w.deletedAt));
      setWsLoad("ready");
    } catch {
      setWorkspaces([]);
      setWsLoad("failed");
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setEmail("");
    setFullName("");
    setRole("member");
    setSubmitting(false);
    setError(null);
    setInviteDone(null);
    setPicked({});
    void loadWorkspaces();
  }, [isOpen, loadWorkspaces]);

  // A viewer in the organization is read-only everywhere, so the server lands them as a
  // workspace viewer whatever is asked; the dialog says so and sends exactly that.
  const orgViewer = role === "viewer";

  function toggleWorkspace(id: string, on: boolean): void {
    setPicked((prev) => {
      const next = { ...prev };
      if (on) next[id] = "workspace_member";
      else delete next[id];
      return next;
    });
  }

  async function performInvite(): Promise<void> {
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }

    const nameTrim = fullName.trim();
    const orgRole = role === "admin" ? "ADMIN" : role === "viewer" ? "VIEWER" : "MEMBER";

    setSubmitting(true);
    try {
      const assignments = Object.entries(picked).map(([workspaceId, wsRole]) => ({
        workspaceId,
        role: (orgViewer ? "workspace_viewer" : wsRole) as InviteWorkspaceRole,
      }));
      const res = await administrationApi.inviteOrgUserV1({
        email: trimmed,
        fullName: nameTrim || undefined,
        orgRole,
        ...(assignments.length > 0 ? { workspaceAssignments: assignments } : {}),
      });
      setInviteDone({ email: res.email, expiresAt: res.expiresAt ?? null });
      onSuccess?.();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { code?: string; message?: string } } };
      const code = ax?.response?.data?.code;
      const mapped = getApiErrorMessage(ax?.response?.data);
      if (
        code === "SEAT_LIMIT_EXCEEDED" ||
        code === "PLAN_SEAT_CAP" ||
        /seat/i.test(mapped) ||
        /plan/i.test(mapped)
      ) {
        setError(
          "Your organization has reached its seat limit. Upgrade your plan to invite more people.",
        );
      } else {
        setError(mapped || "Invite failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }

    await performInvite();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite organization member" size="md">
      {inviteDone ? (
        <div className="space-y-3">
          {inviteDone.expiresAt ? (
            <>
              <p className="text-sm text-green-800">
                Invitation sent to <span className="font-medium">{inviteDone.email}</span>.
              </p>
              <p className="text-xs text-gray-500">
                Expires {new Date(inviteDone.expiresAt).toLocaleString()}
              </p>
            </>
          ) : (
            // Already a member: the server applied the workspace access directly, no invite row.
            <p className="text-sm text-green-800" data-testid="invite-applied-directly">
              <span className="font-medium">{inviteDone.email}</span> is already in your organization.
              Their workspace access was added; no invitation was needed.
            </p>
          )}
          <div className="flex justify-end">
            <Button type="button" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          ) : null}
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="invite-email">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="invite-name">
              Full name
            </label>
            <input
              id="invite-name"
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(ev) => setFullName(ev.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Display name for your records. Final profile name is confirmed when the invite is
              accepted.
            </p>
          </div>
          <div>
            <span id="invite-role-label" className="block text-sm font-medium text-gray-700">
              Organization role
            </span>
            <select
              id="invite-role"
              aria-labelledby="invite-role-label"
              value={role}
              onChange={(e) => setRole(e.target.value as InviteOrgRole)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {role === "admin"
                ? "Admins can invite people, change roles and manage organization settings."
                : "Members collaborate in the workspaces they are added to. Viewers have read-only access."}
            </p>
          </div>
          <div data-testid="invite-workspaces">
            <span className="block text-sm font-medium text-gray-700">Workspaces (optional)</span>
            {wsLoad === "loading" ? (
              <p className="mt-1 text-xs text-gray-500" data-testid="invite-workspaces-loading">
                Loading workspaces…
              </p>
            ) : wsLoad === "failed" ? (
              <UnavailablePanel
                onRetry={() => void loadWorkspaces()}
                testId="invite-workspaces-unavailable"
              />
            ) : workspaces.length === 0 ? (
              <p className="mt-1 text-xs text-gray-500" data-testid="invite-workspaces-empty">
                Your organization has no workspaces yet. The person joins the organization only.
              </p>
            ) : (
              <>
                <p className="mt-1 text-xs text-gray-500">
                  {orgViewer
                    ? "Viewers are read-only in every workspace, so they are added as viewers."
                    : "Pick the workspaces to add them to and their role in each. Access starts when they accept."}
                </p>
                <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto" data-testid="invite-workspace-list">
                  {workspaces.map((w) => {
                    const on = w.id in picked;
                    return (
                      <li key={w.id} className="flex items-center justify-between gap-2 text-sm">
                        <label className="flex min-w-0 items-center gap-2">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(e) => toggleWorkspace(w.id, e.target.checked)}
                            data-testid={`invite-ws-${w.id}`}
                          />
                          <span className="truncate">{w.name}</span>
                        </label>
                        <select
                          aria-label={`Role in ${w.name}`}
                          disabled={!on || orgViewer}
                          value={orgViewer ? "workspace_viewer" : (picked[w.id] ?? "workspace_member")}
                          onChange={(e) =>
                            setPicked((prev) => ({
                              ...prev,
                              [w.id]: e.target.value as InviteWorkspaceRole,
                            }))
                          }
                          className="rounded-md border border-gray-300 px-2 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                          data-testid={`invite-ws-role-${w.id}`}
                        >
                          <option value="workspace_member">Member</option>
                          <option value="workspace_viewer">Viewer</option>
                        </select>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
